import { useRef, useState } from "react";
import JoditEditor from "jodit-react";
import { BsUpload, BsImage } from "react-icons/bs";
import { useFirebase } from "../../Context/FirebaseContext";
import { CircularProgress } from "@mui/material";

const BlogForm = ({
  handleChange,
  handleCheckbox,
  setContent,
  content,
  categories,
  setFeaturedImage,
}) => {
  const editor = useRef(null);
  const firebase = useFirebase();
  const [imageLabel, setImageLabel] = useState("Upload Banner");
  const [url, setUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const isImageFile = (file) => {
    return file && file.type.startsWith("image/");
  };

  const handleImageUpload = async (file) => {
    setIsLoading(true);
    const uploadedUrl = await firebase.handleImageUpload(file);
    setFeaturedImage(uploadedUrl);
    if (uploadedUrl) {
      firebase.getImageUrl(uploadedUrl).then((fetchedUrl) => {
        setUrl(fetchedUrl);
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    // check file size is less than 1MB
    if (file.size > 1 * 1024 * 1024) {
      setImageLabel("FILE TOO LARGE.");
      return;
    }

    if (isImageFile(file)) {
      setImageLabel(file.name.toUpperCase());
      handleImageUpload(file);
    } else {
      setImageLabel("INVALID FORMAT.");
    }
  };

  const editorConfig = {
    readonly: false,
    placeholder: "INPUT DATA HERE...",
    hidePoweredByJodit: true,
    height: "min-h-[500px]",
    toolbarButtonSize: "large",
    buttons:
      "bold,italic,underline,strikethrough,ul,ol,font,fontsize,paragraph,image,link,align,undo,redo,fullsize,source,table,hr,eraser,copyformat,indent,outdent,superscript,subscript,quote,symbols,video,file",
    theme: "light",
    spellcheck: true,
    style: {
      background: "transparent",
      color: "inherit",
      border: "none",
      fontFamily: "monospace",
      textTransform: "uppercase",
    },
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-[90px] mb-32 font-sans">
      {/* Sticky Action Bar */}
      <div className="sticky top-20 z-40 bg-white dark:bg-zinc-800 border-2 border-black dark:border-white/20 px-6 py-4 mb-10 flex flex-wrap items-center justify-between gap-4 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
        <div className="flex items-center gap-4">
          <label
            htmlFor="featuredImage"
            className="group cursor-pointer flex items-center gap-2 px-6 py-3 border border-black dark:border-white/20 bg-[#f4f4f4] dark:text-white dark:bg-zinc-700 hover:bg-black hover:text-white dark:hover:text-indigo-400 transition-colors text-[10px] font-black uppercase tracking-widest text-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-0.5 active:translate-y-0.5"
          >
            <BsImage className="text-sm" />
            <span className="truncate max-w-[150px] sm:max-w-xs">
              {imageLabel}
            </span>
            <input
              type="file"
              className="hidden"
              name="featuredImage"
              id="featuredImage"
              onChange={handleFileChange}
              accept="image/*"
            />
          </label>
        </div>

        <button
          type="submit"
          className="flex items-center gap-3 px-8 py-3 text-[10px] font-black uppercase tracking-widest text-white bg-black hover:bg-green-600 border border-black dark:border-white/20 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] disabled:shadow-none active:shadow-[0_0_0_0_rgba(0,0,0,1)] active:translate-x-1 active:translate-y-1"
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress color="inherit" size={16} thickness={4} />
          ) : (
            <>
              Publish Blog <BsUpload className="text-base" />
            </>
          )}
        </button>
      </div>

      {/* Editor Main Content Area */}
      <div className="bg-white dark:bg-zinc-800 border-2 border-black dark:border-white/20 shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] relative">
        <div className="absolute top-0 right-0 bg-black text-white px-2 py-1 text-[8px] font-mono tracking-widest border-l border-b border-black dark:border-white/20 uppercase z-10 font-bold">
          CREATION STUDIO
        </div>

        {/* Cover Image Presentation */}
        {url && (
          <div className="relative w-full aspect-[21/9] bg-white dark:bg-zinc-800 border-b-2 border-black dark:border-white/20">
            <div className="absolute top-0 left-0 bg-white text-black font-mono text-[10px] font-bold px-2 py-1 tracking-widest z-10 border-b border-r border-black dark:border-white/20 uppercase">
              FIG. 01 — COVER PREVIEW
            </div>
            {isLoading ? (
              <div className="w-full h-full bg-gray-200 animate-pulse pattern-boxes pattern-gray-300 pattern-bg-white pattern-size-4 opacity-50"></div>
            ) : (
              <img
                className="object-cover w-full h-full filter grayscale"
                alt="Cover representation"
                src={url}
              />
            )}
          </div>
        )}

        <div className="p-8 md:p-12 border-b-2 border-black dark:border-white/20">
          {/* Title Input */}
          <textarea
            type="text"
            name="title"
            id="title"
            placeholder="DECLARE TITLE HERE..."
            className="w-full resize-none outline-none bg-transparent text-black dark:text-white font-black text-4xl sm:text-6xl lg:text-7xl placeholder-gray-300 uppercase leading-[0.9] tracking-tighter mb-8 pb-8 border-b-2 border-black dark:border-white/20 border-dashed"
            onChange={handleChange}
            rows={1}
            onInput={(e) => {
              e.target.style.height = "auto";
              e.target.style.height = e.target.scrollHeight + "px";
            }}
            required
            autoFocus
          />

          {/* Jodit Editor Wrapper */}
          <div className="prose prose-lg max-w-none w-full !border-none custom-editor-wrapper">
            <div className="text-[10px] font-mono uppercase tracking-widest font-bold text-gray-500 mb-4">
              BEGIN CONTENT ENTRY:
            </div>
            <JoditEditor
              ref={editor}
              config={editorConfig}
              value={content}
              tabIndex={1}
              onBlur={(newContent) => setContent(newContent)}
            />
          </div>
        </div>

        {/* Categories Section */}
        <div className="bg-[#f4f4f4] dark:bg-zinc-800 p-8 md:p-12">
          <h3 className="text-xs font-black uppercase tracking-widest text-black dark:text-white mb-6 font-mono flex items-center gap-2">
            <span className="w-2 h-2 bg-black dark:bg-white inline-block"></span>
            CATEGORIES
          </h3>
          <div className="flex flex-wrap gap-3">
            {categories.map((c) => (
              <label key={c._id} className="relative cursor-pointer">
                <input
                  id={c.name}
                  name={c.name}
                  type="checkbox"
                  className="peer sr-only"
                  onChange={handleCheckbox}
                />
                <div className="flex items-center gap-2 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-black dark:text-white bg-white dark:bg-zinc-800 border-2 border-black dark:border-white/20 transition-all hover:-translate-y-1 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:hover:shadow-[4px_4px_0px_0px_rgba(255,255,255,0.3)] peer-checked:bg-black peer-checked:text-white dark:peer-checked:bg-white dark:peer-checked:text-black peer-checked:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:peer-checked:shadow-[4px_4px_0px_0px_rgba(255,255,255,0.3)] peer-checked:-translate-y-1 select-none">
                  {c.name}
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Global styles override for Jodit wrapper */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .custom-editor-wrapper .jodit-container {
          border: 2px solid black !important;
          background: white !important;
          font-family: inherit !important;
        }
        .custom-editor-wrapper .jodit-workplace {
          background: transparent !important;
          border-top: 2px solid black !important;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace !important;
        }
        .custom-editor-wrapper .jodit-toolbar__box {
          background: #f4f4f4 !important;
        }
        .custom-editor-wrapper .jodit-wysiwyg {
          padding: 24px !important;
          background: transparent !important;
        }
      `,
        }}
      />
    </div>
  );
};

export default BlogForm;
